import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { LanguageSelect, useLocale } from '../../locale';

// 캐스트 첫 화면. 예전에는 열자마자 화상 회의가 떴다 — 처음 온 사람은
// 여기가 무엇을 하는 곳인지 모른 채 카메라 권한부터 물어봐야 했다.
// 회의는 /meet 으로 옮기고, 이 자리는 제품을 보여 주고 받아 가게 한다.
//
// 글과 차림새는 앤츠미팅 브로슈어를 그대로 따랐다. 같은 제품을 두 곳에서
// 다르게 말하면 어느 쪽이 맞는지 아무도 모르게 된다.
//
// 회의 화면은 사진 대신 여기서 그린다. 판이 바뀌면 사진은 옛것이 되는데
// 그걸 갱신하는 사람은 없다. 그린 것은 늘 지금 모습이고 파일도 안 는다.

const STORE_URL = 'https://apps.microsoft.com/detail/9N6Z6WN1ZGGW?hl=ko-kr&gl=KR&ocid=pdpshare';

// 못 받아왔을 때 보여 줄 값. 화면이 비는 것보다 낫다.
const FALLBACK = Object.freeze({
  version: '1.0.4',
  download_url: 'https://file.skynet.re.kr/biz_antsnest/f20260519_knwljk.Msix',
  file_name: '앤츠미팅.Msix',
  file_size: '8063613',
});

function readableSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  const mb = n / 1024 / 1024;
  return mb >= 1 ? `${mb.toFixed(1)}MB` : `${Math.round(n / 1024)}KB`;
}

/* ── 아이콘: 그림 파일 대신 선으로 그린다.
   확대해도 안 뭉개지고, 색을 글자색에 맞출 수 있다. ── */
const S = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
       strokeLinecap="round" strokeLinejoin="round" {...props} />
);
const IcBolt = (p) => <S {...p}><path d="M13 2 4.5 13.5H11l-1 8.5L19.5 10H13z" /></S>;
const IcRemote = (p) => <S {...p}><rect x="4" y="4" width="16" height="12" rx="2" /><path d="M8 20h8M12 16v4" /><circle cx="12" cy="10" r="2.4" /></S>;
const IcScreen = (p) => <S {...p}><rect x="2.5" y="4.5" width="19" height="13" rx="2" /><path d="M8 21h8M12 17.5V21" /></S>;
const IcWave = (p) => <S {...p}><path d="M3 12h2l2-6 3 13 3-16 3 12 2-3h3" /></S>;
const IcUsers = (p) => <S {...p}><circle cx="9" cy="8" r="3.2" /><path d="M2.8 20a6.2 6.2 0 0 1 12.4 0" /><path d="M16 5.3a3.2 3.2 0 0 1 0 5.4M17.6 14.4A6.2 6.2 0 0 1 21.2 20" /></S>;
const IcLock = (p) => <S {...p}><rect x="4.5" y="10" width="15" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></S>;
const IcWrench = (p) => <S {...p}><path d="M15.5 7.5a4 4 0 0 1-5.2 5.2L5 18l1 1 5.3-5.3a4 4 0 0 0 5.2-5.2l-2 2-1.9-1.9z" /></S>;
const IcBoard = (p) => <S {...p}><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M12 16v4M8 20h8M7.5 11l2.5-2.5 2 2 4-4" /></S>;
const IcChat = (p) => <S {...p}><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-5.1A8 8 0 1 1 21 12z" /></S>;
const IcHeadset = (p) => <S {...p}><path d="M4 13a8 8 0 0 1 16 0" /><rect x="2.5" y="13" width="4" height="6" rx="1.6" /><rect x="17.5" y="13" width="4" height="6" rx="1.6" /><path d="M19.5 19v.6a2.4 2.4 0 0 1-2.4 2.4H13" /></S>;
const IcWindows = (p) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
    <path d="M3 5.6 10.2 4.6v7H3zM11.3 4.4 21 3v8.6h-9.7zM3 12.4h7.2v7L3 18.4zM11.3 12.4H21V21l-9.7-1.4z" />
  </svg>
);
const IcBrowser = (p) => <S {...p}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.6 2.5 15.4 0 18M12 3c-2.5 2.6-2.5 15.4 0 18" /></S>;
const IcArrow = (p) => <S {...p}><path d="M5 12h13M13 6l6 6-6 6" /></S>;
const IcCheck = (p) => <S {...p}><path d="m5 12.5 4.5 4.5L19 7.5" /></S>;
const IcShield = (p) => <S {...p}><path d="M12 3 5 6v6c0 4.2 2.9 7.8 7 9 4.1-1.2 7-4.8 7-9V6z" /><path d="m9 12 2 2 4-4" /></S>;
const IcMic = (p) => <S {...p}><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></S>;
const IcCam = (p) => <S {...p}><rect x="2.5" y="6" width="13" height="12" rx="2" /><path d="m15.5 11 6-3.5v9L15.5 13z" /></S>;
const IcMonitor = (p) => <S {...p}><rect x="2.5" y="4" width="19" height="13" rx="2" /><path d="M8 21h8M12 17v4" /></S>;

const FEATURES = [
  { Icon: IcBolt, title: '초저지연 연결', desc: 'QUIC 기반 P2P 기술로 지연 없는 빠른 연결' },
  { Icon: IcRemote, title: '원격 제어 최적화', desc: '부드럽고 정확한 제어로 실제 작업처럼 편리하게' },
  { Icon: IcScreen, title: '고화질 화면 공유', desc: '선명한 화질과 안정적인 화면 공유 지원' },
  { Icon: IcWave, title: '선명한 음성 품질', desc: 'AI 기반 노이즈 제거로 명확한 음성 전달' },
  { Icon: IcUsers, title: '참가자 관리', desc: '참가자 권한 관리 및 실시간 채팅 지원' },
  { Icon: IcLock, title: '안전한 보안', desc: '데이터 암호화 및 권한 기반 보안 제어' },
];

const USES = [
  { Icon: IcWrench, title: '원격 기술 지원', desc: '원격으로 문제를 진단하고 신속하게 해결', hue: 214 },
  { Icon: IcBoard, title: '온라인 교육', desc: '강의 자료 공유 및 실시간 상호작용 지원', hue: 198 },
  { Icon: IcChat, title: '팀 협업 회의', desc: '프로젝트 공유 및 효율적인 커뮤니케이션', hue: 228 },
  { Icon: IcHeadset, title: '고객 지원 · 유지보수', desc: '고객 시스템 원격 접속 및 신속한 문제 해결', hue: 186 },
];

const STEPS = [
  { no: 1, title: '이름 입력', desc: '표시할 이름을 입력하세요. (예: 홍길동)' },
  { no: 2, title: '방 만들기 또는 참여', desc: '새 방을 만들거나 6자리 코드를 입력해 참여하세요.' },
  { no: 3, title: '바로 연결 시작', desc: '화면 공유, 원격 제어, 채팅 등 모든 기능을 바로 사용하세요.' },
];

const TECH = [
  '서버를 거치지 않는 직접 연결이므로 빠르고 안정적입니다.',
  '네트워크 환경에 최적화하여 끊김을 최소화합니다.',
  '권한 기반 제어로 보안 걱정 없이 안심하고 사용합니다.',
];

export default function Landing() {
  const { t } = useLocale();
  const [release, setRelease] = useState(FALLBACK);

  useEffect(() => {
    let alive = true;
    // 버전은 릴리스 목록 한 곳에서만 관리한다. 여기 적어 두면 새 판을
    // 올릴 때마다 이 화면도 같이 고쳐야 하고, 반드시 잊는다.
    fetch('/api/releases?product=antsmeeting')
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then((body) => {
        const list = Array.isArray(body?.releases) ? body.releases : [];
        const win = list.find((r) => r.platform === 'windows' && r.is_active !== false) || list[0];
        if (alive && win && win.download_url) setRelease(win);
      })
      .catch(() => { /* 못 받아오면 위 기본값 그대로 */ });
    return () => { alive = false; };
  }, []);

  const size = readableSize(release.file_size);

  return (
    <Page>
      <GlobalReset />

      <Nav>
        <NavInner>
          <Logo>
            <LogoMark>A</LogoMark>
            <LogoText>
              <LogoName>AntsMeeting</LogoName>
              <LogoBy>by AntsNest</LogoBy>
            </LogoText>
          </Logo>
          <NavMenu>
            <NavRouteLink to="/drama">{t('AI 작품관')}</NavRouteLink>
            <NavLink href="#features">{t('기능')}</NavLink>
            <NavLink href="#uses">{t('활용')}</NavLink>
            <NavLink href="#how">{t('사용 방법')}</NavLink>
            <NavLink href="#download">{t('다운로드')}</NavLink>
          </NavMenu>
          <LanguageSelect />
          <NavStart to="/meet">{t('회의 시작')} <IcArrow width="16" height="16" /></NavStart>
        </NavInner>
      </Nav>

      <Hero>
        <Mesh aria-hidden="true" />
        <HeroInner>
          <HeroCopy>
            <Title>{t('빠르게 시작하고,')}<br />{t('더 명확하게 연결되는 미팅')}</Title>
            <Sub>{t('원격제어와 화상미팅을 하나로 담은 협업 솔루션')}</Sub>
            <Body>
              {t('이름과 코드만으로 즉시 연결하고,')}<br />
              {t('선명한 화상·음성, 끊김 없는 원격 제어로')}<br />
              {t('어디서든 생산적인 협업을 경험하세요.')}
            </Body>
            <Actions>
              <Primary to="/meet">{t('회의 시작하기')} <IcArrow width="18" height="18" /></Primary>
              <Secondary href={release.download_url}>
                <IcWindows width="20" height="20" />
                <SecText>
                  <span>{t('Windows 앱 내려받기')}</span>
                  <Small>v{release.version}{size ? ` · ${size}` : ''}</Small>
                </SecText>
              </Secondary>
            </Actions>
          </HeroCopy>

          {/* 회의 화면. 사진이 아니라 그린 것이라 늘 지금 모습이다. */}
          <Shot aria-hidden="true">
            <ShotTop>
              <ShotId>ES902872 · v1.0.4</ShotId>
              <ShotTags>
                <Tag>{t('번역중')}</Tag><Tag>{t('원어')}</Tag><Tag $ok>{t('Connected')}</Tag><Tag>00:00:05</Tag>
              </ShotTags>
            </ShotTop>
            <ShotBody>
              <Screen>
                <ScreenHead>{t('미팅룸')}<ScreenSub>{t('QUIC audio-only · 참가자 1')}</ScreenSub></ScreenHead>
                <ScreenName>용수현</ScreenName>
                <ScreenArea><PersonMark /></ScreenArea>
              </Screen>
              <Side>
                <SideBox>
                  <SideTitle>{t('Participants')}<SideCount>1</SideCount></SideTitle>
                  <Person><PAvatar>용</PAvatar><PName>용수현<PRole>{t('내 자신')}</PRole></PName></Person>
                </SideBox>
                <SideBox $grow>
                  <SideTitle>{t('채팅')}</SideTitle>
                  <Empty>{t('아직 메시지가 없습니다.')}</Empty>
                  <InputRow><InputFake>{t('메시지를 입력하세요')}</InputFake><SendBtn><IcArrow width="13" height="13" /></SendBtn></InputRow>
                </SideBox>
              </Side>
            </ShotBody>
            <ShotBar>
              <BarUser><PAvatar $sm>용</PAvatar>용수현</BarUser>
              <BarCtl><IcMic width="14" height="14" />{t('마이크 끄기')}</BarCtl>
              <BarCtl $on><IcCam width="14" height="14" />{t('카메라 켜짐')}</BarCtl>
              <BarCtl><IcMonitor width="14" height="14" />{t('화면 공유')}</BarCtl>
              <BarLeave>{t('나가기')}</BarLeave>
            </ShotBar>
          </Shot>
        </HeroInner>

        <Band>
          <IcShield width="20" height="20" />
          {t('설치 부담 없이, 이름과 코드만으로 빠르게 연결')}
        </Band>
      </Hero>

      <Section id="features">
        <Rule><RuleTitle>{t('AntsMeeting 의 강력한 기능')}</RuleTitle></Rule>
        <FeatureGrid>
          {FEATURES.map(({ Icon, title, desc }) => (
            <Feature key={title}>
              <FIcon><Icon width="26" height="26" /></FIcon>
              <FTitle>{t(title)}</FTitle>
              <FDesc>{t(desc)}</FDesc>
            </Feature>
          ))}
        </FeatureGrid>
      </Section>

      <Section id="uses">
        <Center><H2>{t('다양한 환경에서 활용하세요')}</H2></Center>
        <UseGrid>
          {USES.map(({ Icon, title, desc, hue }) => (
            <Use key={title}>
              <UseArt $hue={hue}><Icon width="34" height="34" /></UseArt>
              <UseTitle>{t(title)}</UseTitle>
              <UseDesc>{t(desc)}</UseDesc>
            </Use>
          ))}
        </UseGrid>
      </Section>

      <Section id="how">
        <TwoCol>
          <Panel>
            <PanelTitle>{t('간단한 3단계 사용 방법')}</PanelTitle>
            <Steps>
              {STEPS.map((s) => (
                <Step key={s.no}>
                  <StepNo $n={s.no}>{s.no}</StepNo>
                  <StepBody>
                    <StepTitle>{t(s.title)}</StepTitle>
                    <StepDesc>{t(s.desc)}</StepDesc>
                  </StepBody>
                </Step>
              ))}
            </Steps>
          </Panel>

          <Panel>
            <PanelTitle>{t('안정적이고 안전한 연결 기술')}</PanelTitle>
            <Diagram>
              <Node><IcMonitor width="28" height="28" /><NodeName>{t('내 PC')}</NodeName></Node>
              <Link2>
                <Dots><i /><i /><i /><i /></Dots>
                <Quic><IcLock width="15" height="15" />QUIC P2P<QuicSub>{t('직접 연결')}</QuicSub></Quic>
              </Link2>
              <Node><IcMonitor width="28" height="28" /><NodeName>{t('상대 PC')}</NodeName></Node>
            </Diagram>
            <Checks>
              {TECH.map((text) => (
                <CheckRow key={text}><Chk><IcCheck width="13" height="13" /></Chk>{t(text)}</CheckRow>
              ))}
            </Checks>
          </Panel>
        </TwoCol>
      </Section>

      <Section id="download">
        <Center><H2>{t('내려받기')}</H2>
          <Lead>{t('브라우저만 있으면 바로 시작할 수 있습니다. 원격 제어까지 쓰려면 Windows 앱을 받으세요.')}</Lead>
        </Center>
        <DownRow>
          <DownCard $primary>
            <DownHead>
              <DownIcon $primary><IcWindows width="24" height="24" /></DownIcon>
              <div>
                <DownName>Windows</DownName>
                <DownMeta>{release.file_name || '앤츠미팅.Msix'}{size ? ` · ${size}` : ''} · v{release.version}</DownMeta>
              </div>
            </DownHead>
            <DownPoint>{t('원격 제어 · 고화질 화면 공유 · 화상 회의 전부')}</DownPoint>
            <BtnFill href={release.download_url}>{t('직접 다운로드')}</BtnFill>
            <BtnLine href={STORE_URL} target="_blank" rel="noreferrer">{t('Microsoft Store 에서 받기')}</BtnLine>
          </DownCard>

          <DownCard>
            <DownHead>
              <DownIcon><IcBrowser width="24" height="24" /></DownIcon>
              <div>
                <DownName>{t('브라우저')}</DownName>
                <DownMeta>{t('설치 없음 · Chrome·Edge 권장')}</DownMeta>
              </div>
            </DownHead>
            <DownPoint>{t('이름과 코드만 넣으면 바로 연결됩니다')}</DownPoint>
            <BtnFill as={Link} to="/meet">{t('지금 회의 시작')}</BtnFill>
            <BtnGhost>{t('내려받을 것이 없습니다')}</BtnGhost>
          </DownCard>
        </DownRow>
      </Section>

      <Closing>
        <ClosingLeft>
          <ClosingTitle>{t('앤츠미팅으로 협업의 새로운 경험을 시작하세요!')}</ClosingTitle>
          <ClosingText>
            {t('빠르고, 안전하고, 간편한 차세대 협업 솔루션')}<br />
            {t('앤츠미팅이 여러분의 업무와 소통을 혁신합니다.')}
          </ClosingText>
        </ClosingLeft>
        <ClosingRight>
          <Logo $light>
            <LogoMark $light>A</LogoMark>
            <LogoText>
              <LogoName $light>AntsMeeting</LogoName>
              <LogoBy $light>by AntsNest</LogoBy>
            </LogoText>
          </Logo>
          <Primary to="/meet">{t('회의 시작하기')} <IcArrow width="18" height="18" /></Primary>
        </ClosingRight>
      </Closing>

      <Footer>
        <span>{t('앤츠네스트 · AntsMeeting')}</span>
        <FooterLinks>
          <a href="https://antsnest.co.kr/Download">{t('앤츠네스트 다운로드')}</a>
          <a href="https://antsnest.co.kr">{t('앤츠네스트')}</a>
        </FooterLinks>
      </Footer>
    </Page>
  );
}

/* ─────────────────────────── 모양 ─────────────────────────── */

const C = {
  bg: '#F5F8FD',
  card: '#FFFFFF',
  navy: '#1A253B',
  navy2: '#25314F',
  blue: '#2F62E8',
  blueSoft: '#EAF0FE',
  line: '#E3EAF5',
  dim: '#69748C',
  dark: '#111B30',   // 제품 화면이 쓰는 색
  dark2: '#0D1526',
  green: '#16A34A',
  teal: '#14B8A6',
};

const dash = keyframes`
  0% { opacity: .25 } 50% { opacity: 1 } 100% { opacity: .25 }
`;

const GlobalReset = createGlobalStyle`
  body { margin: 0; background: ${C.bg}; }
`;

const Page = styled.div`
  min-height: 100vh; overflow-x: hidden;
  background: ${C.bg};
  color: ${C.navy};
  font-family: 'Pretendard-Regular', 'Noto Sans KR', sans-serif;
  -webkit-font-smoothing: antialiased;
`;

/* ── 상단 ── */
const Nav = styled.nav`
  position: sticky; top: 0; z-index: 30;
  background: rgba(245,248,253,.88);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid ${C.line};
`;
const NavInner = styled.div`
  max-width: 1120px; margin: 0 auto; padding: 12px 22px;
  display: flex; align-items: center; gap: 26px; flex-wrap: wrap;
`;
const Logo = styled.div`display: flex; align-items: center; gap: 10px; margin-right: auto;`;
const LogoMark = styled.div`
  width: 32px; height: 32px; border-radius: 9px; flex: none;
  display: grid; place-items: center;
  background: ${(p) => (p.$light ? 'rgba(255,255,255,.16)' : C.navy)};
  color: #fff; font-family: 'Pretendard-SemiBold', sans-serif; font-size: 16px;
`;
const LogoText = styled.div`display: flex; flex-direction: column; line-height: 1.15;`;
const LogoName = styled.span`
  font-family: 'Pretendard-SemiBold', sans-serif; font-size: 16px; letter-spacing: -.3px;
  color: ${(p) => (p.$light ? '#fff' : C.navy)};
`;
const LogoBy = styled.span`
  font-size: 10.5px; color: ${(p) => (p.$light ? 'rgba(255,255,255,.7)' : C.dim)};
`;
const NavMenu = styled.div`
  display: flex; gap: 24px;
  @media (max-width: 860px) { display: none; }
`;
const NavLink = styled.a`
  color: ${C.navy2} !important; text-decoration: none; font-size: 14px;
  opacity: 1;
  &:visited { color: ${C.navy2} !important; }
  &:hover { color: ${C.blue} !important; }
  transition: 120ms;
`;
const NavRouteLink = styled(Link)`
  color: ${C.blue} !important; text-decoration: none; font-size: 14px;
  font-family: 'Pretendard-SemiBold', sans-serif;
  &:visited { color: ${C.blue} !important; }
  &:hover { color: ${C.navy} !important; }
  transition: 120ms;
`;
const NavStart = styled(Link)`
  display: inline-flex; align-items: center; gap: 6px;
  background: ${C.navy}; color: #fff; text-decoration: none;
  padding: 9px 16px; border-radius: 9px; font-size: 14px;
  font-family: 'Pretendard-SemiBold', sans-serif;
  &:hover { background: ${C.blue}; }
  transition: 140ms;
`;

/* ── 첫 화면 ── */
const Hero = styled.header`position: relative; overflow: hidden;`;

const Mesh = styled.div`
  position: absolute; inset: -120px -200px auto auto; width: 620px; height: 520px;
  pointer-events: none; opacity: .5;
  background:
    radial-gradient(circle at 70% 30%, rgba(47,98,232,.16), transparent 60%),
    radial-gradient(circle at 40% 70%, rgba(20,184,166,.12), transparent 62%);
`;

const HeroInner = styled.div`
  position: relative; max-width: 1120px; margin: 0 auto; padding: 56px 22px 34px;
  display: grid; gap: 42px; align-items: center;
  grid-template-columns: minmax(300px, 1fr) minmax(320px, 1.12fr);
  @media (max-width: 980px) { grid-template-columns: 1fr; padding-top: 40px; }
`;

const HeroCopy = styled.div``;

const Title = styled.h1`
  font-family: 'Pretendard-SemiBold', 'Noto Sans KR', sans-serif;
  font-size: clamp(30px, 4.2vw, 46px); line-height: 1.26; margin: 0 0 16px;
  letter-spacing: -1.4px; color: ${C.navy};
`;
const Sub = styled.p`
  font-family: 'Pretendard-SemiBold', sans-serif;
  color: ${C.blue}; font-size: clamp(15px, 1.7vw, 18px); margin: 0 0 20px;
`;
const Body = styled.p`
  color: ${C.dim}; font-size: 15px; line-height: 1.85; margin: 0 0 30px;
`;
const Actions = styled.div`display: flex; gap: 11px; flex-wrap: wrap;`;

const Primary = styled(Link)`
  display: inline-flex; align-items: center; gap: 8px;
  background: ${C.blue}; color: #fff; text-decoration: none;
  padding: 14px 24px; border-radius: 10px;
  font-family: 'Pretendard-SemiBold', sans-serif; font-size: 15px;
  box-shadow: 0 10px 24px rgba(47,98,232,.26);
  &:hover { filter: brightness(1.06); transform: translateY(-1px); }
  transition: 140ms;
`;
const Secondary = styled.a`
  display: inline-flex; align-items: center; gap: 11px;
  background: #fff; color: ${C.navy}; text-decoration: none;
  padding: 10px 20px; border-radius: 10px; border: 1px solid ${C.line};
  &:hover { border-color: ${C.blue}; transform: translateY(-1px); }
  transition: 140ms;
`;
const SecText = styled.span`
  display: flex; flex-direction: column; line-height: 1.3;
  font-family: 'Pretendard-SemiBold', sans-serif; font-size: 14px;
`;
const Small = styled.span`font-size: 11.5px; color: ${C.dim}; font-family: 'Pretendard-Light', sans-serif;`;

/* ── 제품 화면 ── */
const Shot = styled.div`
  border-radius: 14px; overflow: hidden; background: ${C.dark};
  border: 1px solid #22304D;
  box-shadow: 0 28px 60px rgba(26,37,59,.26);
  font-size: 11px; color: #C6D2E6;
`;
const ShotTop = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 9px 12px; background: #0D1526; border-bottom: 1px solid #1E2B44;
`;
const ShotId = styled.div`color: #7D8CA8; font-size: 10px;`;
const ShotTags = styled.div`display: flex; gap: 5px;`;
const Tag = styled.span`
  padding: 2px 7px; border-radius: 5px; font-size: 9.5px;
  background: ${(p) => (p.$ok ? 'rgba(22,163,74,.18)' : 'rgba(255,255,255,.07)')};
  color: ${(p) => (p.$ok ? '#4ADE80' : '#93A2BD')};
`;
const ShotBody = styled.div`display: grid; grid-template-columns: 1fr 128px; gap: 8px; padding: 9px;`;
const Screen = styled.div`
  background: #060B16; border: 1px solid #1B2740; border-radius: 8px;
  padding: 9px; min-height: 186px; display: flex; flex-direction: column;
`;
const ScreenHead = styled.div`font-size: 10.5px; color: #C6D2E6;`;
const ScreenSub = styled.div`font-size: 8.5px; color: #5E6E8C; margin-top: 2px;`;
const ScreenName = styled.div`
  align-self: flex-start; margin-top: 7px; padding: 2px 7px; border-radius: 4px;
  background: rgba(255,255,255,.06); font-size: 9.5px; color: #A9B7D0;
`;
const ScreenArea = styled.div`flex: 1; display: grid; place-items: center;`;
const PersonMark = styled.div`
  width: 26px; height: 26px; border-radius: 50%;
  border: 1.6px solid #2A3A59;
  position: relative;
  &::after {
    content: ''; position: absolute; left: -8px; right: -8px; top: 26px; height: 13px;
    border-radius: 13px 13px 0 0; border: 1.6px solid #2A3A59; border-bottom: none;
  }
`;
const Side = styled.div`display: flex; flex-direction: column; gap: 8px;`;
const SideBox = styled.div`
  background: #0D1526; border: 1px solid #1B2740; border-radius: 8px; padding: 8px;
  ${(p) => p.$grow && 'flex: 1; display: flex; flex-direction: column;'}
`;
const SideTitle = styled.div`
  display: flex; align-items: center; gap: 5px;
  font-size: 9.5px; color: #93A2BD; margin-bottom: 7px;
`;
const SideCount = styled.span`
  background: rgba(47,98,232,.22); color: #93B4FF;
  border-radius: 4px; padding: 0 4px; font-size: 8.5px;
`;
const Person = styled.div`display: flex; align-items: center; gap: 6px;`;
const PAvatar = styled.div`
  width: ${(p) => (p.$sm ? '16px' : '20px')}; height: ${(p) => (p.$sm ? '16px' : '20px')};
  border-radius: 50%; flex: none; display: grid; place-items: center;
  background: ${C.blue}; color: #fff; font-size: ${(p) => (p.$sm ? '8px' : '9px')};
`;
const PName = styled.div`font-size: 9.5px; color: #D4DEEE; line-height: 1.25;`;
const PRole = styled.div`font-size: 8px; color: #5E6E8C;`;
const Empty = styled.div`flex: 1; font-size: 9px; color: #4E5D79; padding: 6px 0;`;
const InputRow = styled.div`display: flex; gap: 4px; align-items: center;`;
const InputFake = styled.div`
  flex: 1; background: #060B16; border: 1px solid #1B2740; border-radius: 5px;
  padding: 4px 6px; font-size: 8.5px; color: #4E5D79;
`;
const SendBtn = styled.div`
  width: 20px; height: 20px; border-radius: 5px; flex: none;
  display: grid; place-items: center; background: ${C.blue}; color: #fff;
`;
const ShotBar = styled.div`
  display: flex; align-items: center; gap: 7px; flex-wrap: wrap;
  padding: 9px 12px; background: #0D1526; border-top: 1px solid #1E2B44;
`;
const BarUser = styled.div`
  display: flex; align-items: center; gap: 5px; margin-right: auto;
  font-size: 9.5px; color: #C6D2E6;
`;
const BarCtl = styled.div`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 5px 9px; border-radius: 6px; font-size: 9.5px;
  background: rgba(255,255,255,.06);
  color: ${(p) => (p.$on ? '#93B4FF' : '#A9B7D0')};
`;
const BarLeave = styled.div`
  padding: 5px 11px; border-radius: 6px; font-size: 9.5px;
  background: rgba(239,68,68,.16); color: #F87171;
`;

const Band = styled.div`
  max-width: 1120px; margin: 0 auto; padding: 0 22px 46px;
  & { display: flex; }
  display: flex; align-items: center; justify-content: center; gap: 10px;
  color: ${C.navy}; font-family: 'Pretendard-SemiBold', sans-serif; font-size: clamp(14px, 1.8vw, 17px);
  svg { color: ${C.blue}; }
`;

/* ── 본문 공통 ── */
const Section = styled.section`max-width: 1120px; margin: 0 auto; padding: 46px 22px;`;
const Center = styled.div`text-align: center; margin-bottom: 26px;`;
const H2 = styled.h2`
  font-family: 'Pretendard-SemiBold', 'Noto Sans KR', sans-serif;
  font-size: clamp(21px, 2.6vw, 28px); margin: 0 0 8px; letter-spacing: -.8px;
`;
const Lead = styled.p`color: ${C.dim}; font-size: 14.5px; margin: 0;`;

const Rule = styled.div`
  display: flex; align-items: center; gap: 16px; margin-bottom: 26px;
  &::before, &::after { content: ''; flex: 1; height: 1px; background: ${C.line}; }
`;
const RuleTitle = styled.h2`
  font-family: 'Pretendard-SemiBold', sans-serif;
  font-size: clamp(19px, 2.4vw, 25px); margin: 0; letter-spacing: -.6px; overflow-wrap: anywhere;
`;

/* ── 기능 ── */
const FeatureGrid = styled.div`
  background: ${C.card}; border: 1px solid ${C.line}; border-radius: 18px;
  padding: 10px; display: grid;
  grid-template-columns: repeat(auto-fit, minmax(168px, 1fr));
`;
const Feature = styled.div`
  padding: 22px 16px; text-align: center;
  border-right: 1px solid ${C.line};
  &:last-child { border-right: none; }
  @media (max-width: 900px) { border-right: none; border-bottom: 1px solid ${C.line}; }
`;
const FIcon = styled.div`
  width: 46px; height: 46px; margin: 0 auto 12px; border-radius: 12px;
  display: grid; place-items: center;
  background: ${C.blueSoft}; color: ${C.blue};
`;
const FTitle = styled.h3`
  font-family: 'Pretendard-SemiBold', sans-serif; font-size: 14.5px; margin: 0 0 6px;
`;
const FDesc = styled.p`color: ${C.dim}; font-size: 12.5px; line-height: 1.6; margin: 0;`;

/* ── 활용 ── */
const UseGrid = styled.div`
  display: grid; gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
`;
const Use = styled.div`
  background: ${C.card}; border: 1px solid ${C.line}; border-radius: 14px; overflow: hidden;
  &:hover { transform: translateY(-3px); box-shadow: 0 14px 30px rgba(26,37,59,.10); }
  transition: 160ms;
`;
const UseArt = styled.div`
  height: 110px; display: grid; place-items: center; color: #fff;
  background: linear-gradient(140deg, hsl(${(p) => p.$hue} 62% 46%), hsl(${(p) => p.$hue} 58% 30%));
`;
const UseTitle = styled.h3`
  font-family: 'Pretendard-SemiBold', sans-serif; font-size: 15px; margin: 16px 16px 6px;
`;
const UseDesc = styled.p`color: ${C.dim}; font-size: 13px; line-height: 1.65; margin: 0 16px 18px;`;

/* ── 3단계 · 연결 기술 ── */
const TwoCol = styled.div`
  display: grid; gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
`;
const Panel = styled.div`
  background: ${C.card}; border: 1px solid ${C.line}; border-radius: 16px; padding: 26px;
`;
const PanelTitle = styled.h3`
  font-family: 'Pretendard-SemiBold', sans-serif; font-size: 17px; margin: 0 0 20px;
  color: ${C.blue};
`;
const Steps = styled.ol`list-style: none; margin: 0; padding: 0; display: grid; gap: 16px;`;
const Step = styled.li`display: flex; gap: 14px; align-items: flex-start;`;
const StepNo = styled.span`
  flex: none; width: 30px; height: 30px; border-radius: 50%;
  display: grid; place-items: center; color: #fff;
  font-family: 'Pretendard-SemiBold', sans-serif; font-size: 13px;
  background: ${(p) => (p.$n === 1 ? C.blue : p.$n === 2 ? C.teal : '#4F6BD8')};
`;
const StepBody = styled.div``;
const StepTitle = styled.div`
  font-family: 'Pretendard-SemiBold', sans-serif; font-size: 14.5px; margin-bottom: 4px;
`;
const StepDesc = styled.div`color: ${C.dim}; font-size: 13px; line-height: 1.6;`;

const Diagram = styled.div`
  display: flex; align-items: center; gap: 12px; margin-bottom: 20px;
`;
const Node = styled.div`
  flex: none; width: 88px; text-align: center; color: ${C.blue};
`;
const NodeName = styled.div`
  color: ${C.navy}; font-size: 12.5px; margin-top: 6px;
  font-family: 'Pretendard-SemiBold', sans-serif;
`;
const Link2 = styled.div`flex: 1; text-align: center;`;
const Dots = styled.div`
  display: flex; justify-content: center; gap: 5px; margin-bottom: 8px;
  i { width: 5px; height: 5px; border-radius: 50%; background: ${C.blue}; display: block;
      animation: ${dash} 1.6s ease-in-out infinite; }
  i:nth-child(2) { animation-delay: .2s }
  i:nth-child(3) { animation-delay: .4s }
  i:nth-child(4) { animation-delay: .6s }
`;
const Quic = styled.div`
  display: inline-flex; flex-direction: column; align-items: center; gap: 2px;
  color: ${C.navy}; font-family: 'Pretendard-SemiBold', sans-serif; font-size: 12.5px;
  svg { color: ${C.blue}; }
`;
const QuicSub = styled.span`font-size: 10.5px; color: ${C.dim}; font-family: 'Pretendard-Regular', sans-serif;`;

const Checks = styled.div`display: grid; gap: 11px;`;
const CheckRow = styled.div`
  display: flex; gap: 9px; align-items: flex-start;
  color: ${C.navy2}; font-size: 13px; line-height: 1.6;
`;
const Chk = styled.span`
  flex: none; width: 18px; height: 18px; border-radius: 50%; margin-top: 1px;
  display: grid; place-items: center; background: rgba(22,163,74,.12); color: ${C.green};
`;

/* ── 다운로드 ── */
const DownRow = styled.div`
  display: grid; gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));
`;
const DownCard = styled.div`
  display: flex; flex-direction: column; gap: 13px; padding: 26px;
  background: ${C.card}; border-radius: 16px;
  border: 1px solid ${(p) => (p.$primary ? 'rgba(47,98,232,.35)' : C.line)};
  box-shadow: ${(p) => (p.$primary ? '0 12px 30px rgba(47,98,232,.10)' : 'none')};
`;
const DownHead = styled.div`display: flex; align-items: center; gap: 13px;`;
const DownIcon = styled.div`
  width: 44px; height: 44px; border-radius: 11px; flex: none;
  display: grid; place-items: center;
  background: ${(p) => (p.$primary ? C.blueSoft : '#F1F4FA')};
  color: ${(p) => (p.$primary ? C.blue : C.dim)};
`;
const DownName = styled.div`font-family: 'Pretendard-SemiBold', sans-serif; font-size: 17px;`;
const DownMeta = styled.div`color: ${C.dim}; font-size: 12px; margin-top: 3px;`;
const DownPoint = styled.div`
  color: ${C.navy2}; font-size: 13.5px; padding-top: 11px; border-top: 1px solid ${C.line};
`;
const BtnFill = styled.a`
  display: block; text-align: center; text-decoration: none;
  background: ${C.blue}; color: #fff; padding: 13px 18px; border-radius: 10px;
  font-family: 'Pretendard-SemiBold', sans-serif; font-size: 14.5px;
  &:hover { filter: brightness(1.06); }
  transition: 120ms;
`;
const BtnLine = styled.a`
  display: block; text-align: center; text-decoration: none;
  color: ${C.navy}; border: 1px solid ${C.line};
  padding: 11px 18px; border-radius: 10px; font-size: 13.5px;
  &:hover { border-color: ${C.blue}; }
  transition: 120ms;
`;
const BtnGhost = styled.div`text-align: center; color: ${C.dim}; font-size: 12.5px; padding: 11px;`;

/* ── 마무리 ── */
const Closing = styled.section`
  max-width: 1120px; margin: 26px auto 0; padding: 34px 30px;
  border-radius: 18px;
  background: linear-gradient(120deg, ${C.navy}, ${C.navy2});
  color: #fff;
  display: flex; align-items: center; justify-content: space-between; gap: 24px; flex-wrap: wrap;
`;
const ClosingLeft = styled.div``;
const ClosingTitle = styled.h2`
  font-family: 'Pretendard-SemiBold', sans-serif; font-size: clamp(18px, 2.2vw, 23px);
  margin: 0 0 10px; letter-spacing: -.6px;
`;
const ClosingText = styled.p`color: rgba(255,255,255,.72); font-size: 13.5px; line-height: 1.75; margin: 0;`;
const ClosingRight = styled.div`display: flex; flex-direction: column; gap: 16px; align-items: flex-start;`;

const Footer = styled.footer`
  max-width: 1120px; margin: 0 auto; padding: 24px 22px 52px;
  display: flex; justify-content: space-between; flex-wrap: wrap; gap: 10px;
  color: ${C.dim}; font-size: 12.5px;
`;
const FooterLinks = styled.div`
  display: flex; gap: 18px;
  a { color: ${C.dim}; text-decoration: none; }
  a:hover { color: ${C.blue}; }
`;
